// src/pages/AdminSucursales.jsx
import { useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase/firebase.js";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
} from "firebase/firestore";

export default function AdminSucursales() {
  // Estado del usuario (solo para mostrar en UI y evitar warnings)
  const [me, setMe] = useState(null);

  // Listado y selección
  const [sucursales, setSucursales] = useState([]);
  const [selId, setSelId] = useState(""); // id del doc seleccionado (o vacío para "nueva")

  // Formulario
  const [form, setForm] = useState({
    sucursalId: "",
    nombre: "",
    activa: true,
    direccion: {
      calle: "",
      colonia: "",
      alcaldia: "",
      ciudad: "",
      estado: "",
      cp: "",
      pais: "México",
      full: "",
    },
    contacto: {
      email: "",
    },
    telefonos: [],
    whatsapps: [],
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Cargar usuario (solo UI)
  useEffect(() => {
    const u = auth.currentUser;
    if (u) {
      setMe({ uid: u.uid, email: u.email || "" });
    } else {
      setMe(null);
    }
  }, []);

  // Cargar listado de sucursales
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, "sucursales"));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Orden por nombre (o sucursalId si no hay nombre)
        list.sort((a, b) =>
          (a.nombre || a.sucursalId || "").localeCompare(
            b.nombre || b.sucursalId || ""
          )
        );
        setSucursales(list);

        if (list.length > 0) {
          const first = list[0];
          setSelId(first.id);
          setForm({
            sucursalId: first.sucursalId || first.id || "",
            nombre: first.nombre || "",
            activa: !!first.activa,
            direccion: {
              calle: first?.direccion?.calle || "",
              colonia: first?.direccion?.colonia || "",
              alcaldia: first?.direccion?.alcaldia || "",
              ciudad: first?.direccion?.ciudad || "",
              estado: first?.direccion?.estado || "",
              cp: first?.direccion?.cp || "",
              pais: first?.direccion?.pais || "México",
              full: first?.direccion?.full || "",
            },
            contacto: {
              email: first?.contacto?.email || "",
            },
            telefonos: Array.isArray(first?.telefonos) ? first.telefonos : [],
            whatsapps: Array.isArray(first?.whatsapps) ? first.whatsapps : [],
          });
        } else {
          setSelId("");
          setForm((v) => ({
            ...v,
            sucursalId: "",
            nombre: "",
          }));
        }
      } catch (e) {
        console.error("[AdminSucursales] cargar sucursales:", e);
        alert("No se pudo cargar el listado de sucursales.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // no incluyas `db` aquí, no cambia

  // Cuando cambia la selección, carga esa sucursal al form
  useEffect(() => {
    if (!selId) return; // modo "nueva"
    const s = sucursales.find((x) => x.id === selId);
    if (!s) return;
    setForm({
      sucursalId: s.sucursalId || s.id || "",
      nombre: s.nombre || "",
      activa: !!s.activa,
      direccion: {
        calle: s?.direccion?.calle || "",
        colonia: s?.direccion?.colonia || "",
        alcaldia: s?.direccion?.alcaldia || "",
        ciudad: s?.direccion?.ciudad || "",
        estado: s?.direccion?.estado || "",
        cp: s?.direccion?.cp || "",
        pais: s?.direccion?.pais || "México",
        full: s?.direccion?.full || "",
      },
      contacto: {
        email: s?.contacto?.email || "",
      },
      telefonos: Array.isArray(s?.telefonos) ? s.telefonos : [],
      whatsapps: Array.isArray(s?.whatsapps) ? s.whatsapps : [],
    });
  }, [selId, sucursales]);

  const esNueva = useMemo(() => selId === "", [selId]);

  // Handlers
  const onChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith("direccion.")) {
      const field = name.split(".")[1];
      setForm((v) => ({
        ...v,
        direccion: { ...v.direccion, [field]: value },
      }));
      return;
    }
    if (name.startsWith("contacto.")) {
      const field = name.split(".")[1];
      setForm((v) => ({
        ...v,
        contacto: { ...v.contacto, [field]: value },
      }));
      return;
    }

    setForm((v) => ({
      ...v,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onChangeLista = (name, raw) => {
    const arr = (raw || "")
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    setForm((v) => ({ ...v, [name]: arr }));
  };

  const limpiar = () => {
    setSelId("");
    setForm({
      sucursalId: "",
      nombre: "",
      activa: true,
      direccion: {
        calle: "",
        colonia: "",
        alcaldia: "",
        ciudad: "",
        estado: "",
        cp: "",
        pais: "México",
        full: "",
      },
      contacto: { email: "" },
      telefonos: [],
      whatsapps: [],
    });
  };

  const guardar = async () => {
    try {
      setSaving(true);

      const payload = {
        sucursalId: (form.sucursalId || "").trim(),
        nombre: (form.nombre || "").trim(),
        activa: !!form.activa,
        direccion: {
          calle: form.direccion.calle || "",
          colonia: form.direccion.colonia || "",
          alcaldia: form.direccion.alcaldia || "",
          ciudad: form.direccion.ciudad || "",
          estado: form.direccion.estado || "",
          cp: form.direccion.cp || "",
          pais: form.direccion.pais || "México",
          full:
            form.direccion.full ||
            [
              form.direccion.calle,
              form.direccion.colonia,
              form.direccion.alcaldia,
              form.direccion.cp,
              form.direccion.ciudad,
              form.direccion.estado,
            ]
              .filter(Boolean)
              .join(", "),
        },
        contacto: { email: (form.contacto.email || "").trim() },
        telefonos: Array.isArray(form.telefonos) ? form.telefonos : [],
        whatsapps: Array.isArray(form.whatsapps) ? form.whatsapps : [],
        updatedAt: new Date().toISOString(),
      };

      if (!payload.sucursalId) {
        alert("sucursalId es obligatorio.");
        setSaving(false);
        return;
      }
      if (!payload.nombre) {
        alert("El nombre de la sucursal es obligatorio.");
        setSaving(false);
        return;
      }

      const docId = esNueva ? payload.sucursalId : selId; // usamos sucursalId como id de documento cuando se crea
      const ref = doc(db, "sucursales", docId);

      if (esNueva) {
        await setDoc(ref, {
          ...payload,
          createdAt: new Date().toISOString(),
        });
      } else {
        await updateDoc(ref, payload);
      }

      alert("Sucursal guardada.");

      // Recargar listado y mantener selección
      const snap = await getDocs(collection(db, "sucursales"));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) =>
        (a.nombre || a.sucursalId || "").localeCompare(
          b.nombre || b.sucursalId || ""
        )
      );
      setSucursales(list);
      setSelId(docId);
    } catch (e) {
      console.error("[AdminSucursales] guardar:", e);
      alert(e.message || "No se pudo guardar la sucursal.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-600">Cargando…</div>;

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      {/* Topbar */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white flex justify-between items-center">
        <div className="font-semibold">Administrar Sucursales</div>
        <div className="text-sm text-gray-600">Admin · {me?.email || ""}</div>
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista */}
        <aside className="lg:col-span-1">
          <div className="rounded border border-gray-200 bg-white p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Sucursales</h3>
              <button
                onClick={limpiar}
                className="px-2 py-1 rounded bg-gray-900 text-white text-sm hover:bg-gray-800"
              >
                + Nueva
              </button>
            </div>

            <div className="space-y-1 max-h-[60vh] overflow-auto">
              {sucursales.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSelId(s.id)}
                  className={`w-full text-left px-3 py-2 rounded border ${
                    selId === s.id
                      ? "border-gray-900 bg-gray-50"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="font-medium">{s.nombre || s.id}</div>
                  <div className="text-xs text-gray-500">
                    {s.sucursalId} · {s.activa ? "Activa" : "Inactiva"}
                  </div>
                  {s?.direccion?.full && (
                    <div className="text-xs text-gray-400 line-clamp-1">
                      {s.direccion.full}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Form */}
        <main className="lg:col-span-2">
          <div className="rounded border border-gray-200 bg-white p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-sm text-gray-600">sucursalId</label>
                <input
                  name="sucursalId"
                  value={form.sucursalId}
                  onChange={onChange}
                  disabled={!esNueva}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  placeholder="cdmx, gdl, mty…"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm text-gray-600">Nombre</label>
                <input
                  name="nombre"
                  value={form.nombre}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  placeholder="CDMX, Guadalajara, Monterrey…"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div className="md:col-span-1">
                <label className="text-sm text-gray-600">Activa</label>
                <div className="mt-2">
                  <input
                    type="checkbox"
                    name="activa"
                    checked={!!form.activa}
                    onChange={onChange}
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="text-sm text-gray-600">Email contacto</label>
                <input
                  name="contacto.email"
                  type="email"
                  value={form.contacto.email}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  placeholder="tucorreo@gmail.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div className="md:col-span-3">
                <label className="text-sm text-gray-600">Calle</label>
                <input
                  name="direccion.calle"
                  value={form.direccion.calle}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
              <div className="md:col-span-3">
                <label className="text-sm text-gray-600">Colonia</label>
                <input
                  name="direccion.colonia"
                  value={form.direccion.colonia}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Alcaldía</label>
                <input
                  name="direccion.alcaldia"
                  value={form.direccion.alcaldia}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Ciudad</label>
                <input
                  name="direccion.ciudad"
                  value={form.direccion.ciudad}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Estado</label>
                <input
                  name="direccion.estado"
                  value={form.direccion.estado}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">CP</label>
                <input
                  name="direccion.cp"
                  value={form.direccion.cp}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">País</label>
                <input
                  name="direccion.pais"
                  value={form.direccion.pais}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
              <div className="md:col-span-3">
                <label className="text-sm text-gray-600">Dirección (full)</label>
                <input
                  name="direccion.full"
                  value={form.direccion.full}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  placeholder="Calle, colonia, alcaldía, CP, Ciudad, Estado"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-600">
                  Teléfonos (coma o saltos)
                </label>
                <textarea
                  rows={3}
                  value={form.telefonos.join("\n")}
                  onChange={(e) => onChangeLista("telefonos", e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">
                  WhatsApps (coma o saltos)
                </label>
                <textarea
                  rows={3}
                  value={form.whatsapps.join("\n")}
                  onChange={(e) => onChangeLista("whatsapps", e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={guardar}
                disabled={saving}
                className="px-4 py-2 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {saving ? "Guardando…" : esNueva ? "Crear sucursal" : "Guardar cambios"}
              </button>
              {!esNueva && (
                <span className="text-sm text-gray-600">
                  ID doc: <code>{selId}</code>
                </span>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}